/**
 * CommunityServicesSection - Shows relevant community service providers on subcategory pages
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Star, MapPin, ChevronRight, Users, Building, 
  GraduationCap, Search, HeartHandshake, Gift,
  Megaphone, Car, HelpingHand, Loader2, Sparkles,
  Phone, Mail, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map category slugs to service types
const CATEGORY_TO_SERVICE_TYPE = {
  'local-groups-clubs': 'local-groups-clubs',
  'classes-workshops': 'classes-workshops',
  'lost-found': 'lost-found',
  'volunteers': 'volunteers',
  'charity-donations': 'charity-donations',
  'notices-announcements': 'notices-announcements',
  'ride-sharing': 'ride-sharing',
  'community-help': 'community-help',
};

// Category icons
const CATEGORY_ICONS = {
  'local-groups-clubs': Users,
  'classes-workshops': GraduationCap,
  'lost-found': Search,
  'volunteers': HeartHandshake,
  'charity-donations': Gift,
  'notices-announcements': Megaphone,
  'ride-sharing': Car,
  'community-help': HelpingHand,
};

// Service type colors
const SERVICE_COLORS = {
  'local-groups-clubs': 'from-blue-500 to-indigo-600',
  'classes-workshops': 'from-purple-500 to-pink-600',
  'lost-found': 'from-amber-500 to-orange-600',
  'volunteers': 'from-rose-500 to-red-600',
  'charity-donations': 'from-emerald-500 to-teal-600',
  'notices-announcements': 'from-cyan-500 to-blue-600',
  'ride-sharing': 'from-green-500 to-emerald-600',
  'community-help': 'from-violet-500 to-purple-600',
};

// Section titles
const SECTION_TITLES = {
  'local-groups-clubs': 'Community Centers & Clubs',
  'classes-workshops': 'Learning Centers',
  'lost-found': 'Lost & Found Services',
  'volunteers': 'Volunteer Organizations',
  'charity-donations': 'Charitable Organizations',
  'notices-announcements': 'Community Boards',
  'ride-sharing': 'Ride Sharing Services',
  'community-help': 'Help Networks',
};

function ProviderCard({ provider, currencySymbol }) {
  const serviceType = provider.service_type || 'community-help';
  const Icon = CATEGORY_ICONS[serviceType] || Building;
  const gradientClass = SERVICE_COLORS[serviceType] || 'from-gray-500 to-gray-600';
  
  return (
    <Card 
      className="group hover:shadow-lg transition-all border border-gray-200 hover:border-blue-300"
      data-testid={`community-provider-${provider.id}`}
    >
      <div className="relative h-32 overflow-hidden rounded-t-lg">
        {provider.images?.[0] ? (
          <img 
            src={provider.images[0]} 
            alt={provider.provider_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-r ${gradientClass} flex items-center justify-center`}>
            <Icon className="w-12 h-12 text-white/50" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        
        {provider.featured && (
          <Badge className="absolute top-2 left-2 bg-blue-500 text-white">
            Featured
          </Badge>
        )}
        
        {provider.verified && (
          <Badge className="absolute top-2 right-2 bg-green-500 text-white">
            Verified
          </Badge>
        )}
      </div>
      
      <CardContent className="pt-3 pb-4">
        <h3 className="font-bold text-base line-clamp-1">{provider.provider_name}</h3>
        
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
          {provider.description}
        </p>
        
        <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
          {provider.rating > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              {provider.rating?.toFixed(1)}
              {provider.review_count > 0 && (
                <span className="text-gray-400">({provider.review_count})</span>
              )}
            </span>
          )}
          <span className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {provider.location}
          </span>
        </div>
        
        {/* Services offered */}
        {provider.services_offered?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {provider.services_offered.slice(0, 2).map((service, idx) => (
              <Badge key={idx} variant="outline" className="text-xs">
                {service}
              </Badge>
            ))}
            {provider.services_offered.length > 2 && (
              <Badge variant="outline" className="text-xs">
                +{provider.services_offered.length - 2} more
              </Badge>
            )}
          </div>
        )}
        
        <div className="flex items-center justify-between mt-3 pt-2 border-t">
          <div className="flex gap-2">
            {provider.phone && (
              <a 
                href={`tel:${provider.phone}`}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-blue-100 transition-colors"
                title="Call"
              >
                <Phone className="w-4 h-4 text-gray-600" />
              </a>
            )}
            {provider.email && (
              <a 
                href={`mailto:${provider.email}`}
                className="p-1.5 rounded-full bg-gray-100 hover:bg-blue-100 transition-colors"
                title="Email"
              >
                <Mail className="w-4 h-4 text-gray-600" />
              </a>
            )}
          </div>
          {provider.starting_price ? (
            <div className="text-right">
              <p className="text-xs text-gray-500">From</p>
              <p className="font-bold text-blue-600">
                {currencySymbol}{provider.starting_price?.toLocaleString()}
              </p>
            </div>
          ) : (
            <Button size="sm" variant="outline" className="text-blue-600 border-blue-200">
              Contact
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CommunityServicesSection({ categorySlug, country = 'LK', maxProviders = 4 }) {
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  // Get service type for this category
  const serviceType = CATEGORY_TO_SERVICE_TYPE[categorySlug];
  const sectionTitle = SECTION_TITLES[categorySlug] || 'Community Services';
  const Icon = CATEGORY_ICONS[categorySlug] || Users;
  
  useEffect(() => {
    const fetchProviders = async () => {
      if (!serviceType) {
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/api/community/providers?service_type=${serviceType}&country=${country}&limit=${maxProviders}`
        );
        if (response.ok) {
          const data = await response.json();
          setProviders(data.providers || []);
        }
      } catch (error) {
        console.error('Failed to fetch community providers:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProviders();
  }, [categorySlug, country, serviceType, maxProviders]);
  
  // Don't render if not a community category or no providers
  if (!serviceType) return null;
  
  if (loading) {
    return (
      <div className="py-8" data-testid="community-services-loading">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Sparkles className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-bold">{sectionTitle}</h2>
        </div>
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </div>
    );
  }
  
  if (providers.length === 0) return null;
  
  return (
    <div className="py-6" data-testid="community-services-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold">{sectionTitle}</h2>
            <p className="text-sm text-gray-500">Local community services available</p>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/category/community-local-life')}
          className="text-blue-600 border-blue-200 hover:bg-blue-50"
        >
          View All Services
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {providers.map(provider => (
          <ProviderCard
            key={provider.id}
            provider={provider}
            currencySymbol={currencySymbol}
          />
        ))}
      </div>
    </div>
  );
}

// Export helper function to check if a category has community providers
export function isCommunityCategory(slug) {
  return slug in CATEGORY_TO_SERVICE_TYPE;
}
